import type {
    IRegDataComparisonMatchRow,
    IRegDataStagedChange,
    IRegSystemOption,
    IRegTemplateOption,
    IRegDataWarning,
} from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";

export const ReconciliationRowStates = {
    Match: "Match",
    FlagDiff: "FlagDiff",
    TemplateOnly: "TemplateOnly",
    SystemOnly: "SystemOnly",
    InactiveMatch: "InactiveMatch",
    DeletedMatch: "DeletedMatch",
    SystemInactiveOrDeletedOnly: "SystemInactiveOrDeletedOnly",
    NoAction: "NoAction",
} as const;

export type ReconciliationRowState = (typeof ReconciliationRowStates)[keyof typeof ReconciliationRowStates];

export const ReconciliationActionIcons = {
    Match: "match",
    Stage: "stage",
    SoftDelete: "softDelete",
    StagedSync: "stagedSync",
    StagedDelete: "stagedDelete",
    None: "none",
} as const;

export type ReconciliationActionIcon = (typeof ReconciliationActionIcons)[keyof typeof ReconciliationActionIcons];

export interface FieldActionPolicy {
    allowAdd: boolean;
    allowSoftDelete: boolean;
    allowRestore: boolean;
}

export interface ComparatorContext {
    ewcHazardDeterminate?: boolean;
}

export interface TemplateSideData {
    match: IRegDataComparisonMatchRow | null;
    templateOption: IRegTemplateOption | null;
    templateRawValue: string | null;
}

export interface SystemSideData {
    option: IRegSystemOption | null;
    systemRecordId: string | null;
    displayValue: string | null;
}

export interface ReconciliationGridRow {
    rowId: string;
    fieldKey: string;
    normalizedKey: string;
    rowState: ReconciliationRowState;
    actionIcon: ReconciliationActionIcon;
    stageChangeType: string | null;
    actionDisabled: boolean;
    actionDisabledReason?: string;
    lifecycleNote?: string;
    warnings: IRegDataWarning[];
    templateSide: TemplateSideData;
    systemSide: SystemSideData;
    hazardIndeterminate: boolean;
    isStaged: boolean;
    stagedChange?: IRegDataStagedChange;
    stagedChangeKey?: string;
}

export interface ReconciliationCardSummary {
    alignedCount: number;
    needsChangesCount: number;
    toAddCount: number;
    toUpdateCount: number;
    toReactivateCount: number;
    toRestoreCount: number;
    toRemoveCount: number;
}

export interface ReconciliationGridCardModel {
    fieldKey: string;
    label: string;
    summary: ReconciliationCardSummary;
    warnings: IRegDataWarning[];
    gridRows: ReconciliationGridRow[];
    fieldLifecycleNote?: string;
    hazardIndeterminate: boolean;
}

/** Estimated row height for virtualised grid (px); measured rows override via measureElement. */
export const RECONCILIATION_GRID_ROW_HEIGHT = 80;

/** Minimum grid width (px) before horizontal scroll; preserves 5/5/2 column proportions. */
export const RECONCILIATION_GRID_MIN_WIDTH = 720;
